import React, { useEffect } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/app/providers';
import { toast } from 'react-hot-toast';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireAccess?: boolean;
}

/**
 * Guard de route qui vérifie les permissions avant de rendre les composants
 * Attend que les permissions soient complètement chargées pour éviter les faux négatifs
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false,
  requireAccess = false 
}) => {
  const { idorga } = useParams<{ idorga: string }>();
  const location = useLocation();
  const { 
    currentUser, 
    loading: authLoading, 
    isAdmin,
    permissionsInitialized,
    affiliations // Utiliser les affiliations de usePermissions via AuthContext
  } = useAuth();

  // Log de debug pour comprendre l'état des affiliations
  useEffect(() => {
    console.log('🔐 ProtectedRoute - État des affiliations:', {
      hasCurrentUser: !!currentUser,
      authLoading,
      permissionsInitialized,
      affiliationsLength: affiliations?.length || 0,
      affiliations: affiliations,
      idorga,
      pathname: location.pathname
    });
  }, [currentUser, authLoading, permissionsInitialized, affiliations, idorga, location.pathname]);

  // Routes qui sont accessibles même sans affiliation (comme créer une organisation ou voir ses commandes)
  const isCreateOrganizationRoute = location.pathname === '/organizations/create';
  const isMyOrdersRoute = location.pathname === '/my-orders' || location.pathname.startsWith('/my-orders/');

  // Fonction de vérification d'accès temporaire utilisant les bonnes affiliations
  const hasAccessToOrganizationTemp = (orgId: string): boolean => {
    if (!currentUser?.uid || !affiliations) {
      console.log('🔐 ProtectedRoute - hasAccessToOrganizationTemp: conditions non remplies', {
        hasCurrentUser: !!currentUser?.uid,
        hasAffiliations: !!affiliations,
        affiliationsLength: affiliations?.length || 0
      });
      return false;
    }
    
    // Les affiliations sont déjà filtrées par customerId de l'utilisateur connecté
    const hasAccess = affiliations.some(aff => 
      aff.organizationId === orgId &&
      aff.status === 'active'
    );
    
    console.log('🔐 ProtectedRoute - Vérification accès temporaire:', {
      organizationId: orgId,
      customerId: currentUser.uid,
      hasAccess,
      affiliationsCount: affiliations?.length || 0,
      affiliations: affiliations.map(aff => ({
        id: aff.id,
        customerId: aff.customerId,
        organizationId: aff.organizationId,
        role: aff.role,
        status: aff.status
      }))
    });
    
    return hasAccess;
  };

  const isAdminForOrganizationTemp = (orgId: string): boolean => {
    if (!currentUser?.uid || !affiliations) return false;
    
    // Les affiliations sont déjà filtrées par customerId de l'utilisateur connecté
    const affiliation = affiliations.find(aff => 
      aff.organizationId === orgId &&
      aff.status === 'active'
    );
    
    const isAdmin = affiliation ? (affiliation.role === 'admin' || affiliation.role === 'owner') : false;
    
    console.log('🔐 ProtectedRoute - Vérification admin temporaire:', {
      organizationId: orgId,
      userId: currentUser.uid,
      isAdmin,
      affiliation
    });
    
    return isAdmin;
  };

  // Vérifier si l'utilisateur est connecté en premier
  if (!currentUser) {
    console.log('🔐 ProtectedRoute - Utilisateur non connecté');
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 text-red-500">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Accès non autorisé
          </h2>
          <p className="text-gray-600 mb-6">
            Vous devez être connecté pour accéder à cette page.
          </p>
          <button
            onClick={() => globalThis.location.href = '/'}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  // Afficher un écran de chargement tant que les affiliations ne sont pas chargées
  if (authLoading || !permissionsInitialized) {
    console.log('🔐 ProtectedRoute - Écran de chargement:', {
      authLoading,
      permissionsInitialized,
      affiliationsLength: affiliations?.length || 0,
      currentUser: currentUser?.uid || 'null'
    });
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Vérification des permissions...</p>
          <p className="text-gray-400 text-sm mt-2">Chargement des données utilisateur</p>
        </div>
      </div>
    );
  }

  // Si l'utilisateur est connecté mais n'a aucune affiliation, afficher un message d'erreur
  // SAUF pour les routes spéciales comme créer une organisation ou voir ses commandes
  if (!isAdmin && affiliations && affiliations.length === 0 && !isCreateOrganizationRoute && !isMyOrdersRoute) {
    console.log('🔐 ProtectedRoute - Aucune affiliation trouvée', {
      affiliations,
      affiliationsLength: affiliations?.length || 0,
      permissionsInitialized,
      currentUser: currentUser?.uid,
      idorga
    });
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 text-orange-500">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Aucun accès trouvé
          </h2>
          <p className="text-gray-600 mb-6">
            Vous n'avez pas d'accès à cette organisation ou à cette page.
          </p>
          <button
            onClick={() => globalThis.location.href = '/'}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  // Vérifier l'accès à l'organisation si requis
  if (requireAccess && idorga) {
    console.log('🔍 ProtectedRoute - Vérification requireAccess:', {
      requireAccess,
      idorga,
      hasCurrentUser: !!currentUser,
      affiliationsLength: affiliations?.length || 0,
      isAdmin,
    });
    
    // Si super admin plateforme, accès total sans vérifier les affiliations
    if (isAdmin) {
      console.log('✅ ProtectedRoute - Super admin, accès total pour requireAccess');
      return <>{children}</>;
    }
    
    const hasAccess = hasAccessToOrganizationTemp(idorga);
    
    console.log('🔍 ProtectedRoute - Résultat vérification requireAccess:', {
      hasAccess,
      idorga,
      willRedirect: !hasAccess
    });
    
    if (!hasAccess) {
      console.log('❌ ProtectedRoute - Accès refusé pour requireAccess, redirection vers /');
      // Seulement afficher le toastr si les permissions sont initialisées
      if (permissionsInitialized) {
        toast.error("Vous n'avez pas accès à cette organisation");
      }
      return <Navigate to="/" replace />;
    }
    
    console.log('✅ ProtectedRoute - Accès accordé pour requireAccess');
  }

  // Vérifier l'accès au dashboard admin si requis
  // Tous les membres affiliés peuvent accéder au dashboard admin
  // Les permissions granulaires contrôleront ce qu'ils peuvent voir/faire
  if (requireAdmin && idorga) {
    console.log('🔍 ProtectedRoute - Vérification requireAdmin:', {
      requireAdmin,
      idorga,
      hasCurrentUser: !!currentUser,
      affiliationsLength: affiliations?.length || 0,
      isAdmin,
    });
    
    // Super admin: accès complet au dashboard admin
    if (isAdmin) {
      console.log('✅ ProtectedRoute - Super admin, accès total pour requireAdmin');
      return <>{children}</>;
    }
    // Vérifier simplement l'accès à l'organisation (comme requireAccess)
    // Plus besoin de vérifier le rôle admin/owner
    const hasAccess = hasAccessToOrganizationTemp(idorga);
    
    console.log('🔍 ProtectedRoute - Résultat vérification requireAdmin:', {
      hasAccess,
      idorga,
      willRedirect: !hasAccess
    });
    
    if (!hasAccess) {
      console.log('❌ ProtectedRoute - Accès refusé pour requireAdmin, redirection vers /orga/${idorga}');
      // Seulement afficher le toastr si les permissions sont initialisées
      if (permissionsInitialized) {
        toast.error("Vous n'avez pas accès à cette organisation");
      }
      return <Navigate to={`/orga/${idorga}`} replace />;
    }
    
    console.log('✅ ProtectedRoute - Accès accordé pour requireAdmin');
  }

  // Permissions OK, rendre les enfants
  return <>{children}</>;
};

export default ProtectedRoute;

